import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, TrendingDown, Star } from "lucide-react";
import { fmt } from "@/hooks/useRates";
import { useLang } from "@/i18n";

const COPPER_IMG =
  "https://images.pexels.com/photos/11133641/pexels-photo-11133641.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ALU_IMG =
  "https://images.unsplash.com/photo-1697698532634-ea59b636ccea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxhbHVtaW5pdW0lMjBzaGVldHMlMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc4NzAxNzExMXww&ixlib=rb-4.1.0&q=85";
const GOLD_IMG =
  "https://images.unsplash.com/photo-1610375461369-d613b564f4c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxnb2xkJTIwaW5nb3RzJTIwYmFycyUyMHN0YWNrZWR8ZW58MHx8fHwxNzg3MDE1NzkxfDA&ixlib=rb-4.1.0&q=85";

const LAYOUT = [
  { symbol: "ALUMINIUM", image: ALU_IMG, span: "md:col-span-7 md:row-span-2", featured: true, accent: "#C0C0C0" },
  { symbol: "COPPER", image: COPPER_IMG, span: "md:col-span-5 md:row-span-2", featured: true, accent: "#B87333" },
  { symbol: "GOLD", image: GOLD_IMG, span: "md:col-span-4", accent: "#C9A227" },
  { symbol: "SILVER", image: null, span: "md:col-span-4", accent: "#C0C0C0" },
  { symbol: "ZINC", image: null, span: "md:col-span-4", accent: "#8FA3B8" },
  { symbol: "LEAD", image: null, span: "md:col-span-6", accent: "#7D8590" },
  { symbol: "NICKEL", image: null, span: "md:col-span-6", accent: "#9BA8B5" },
];

const MetalCard = ({ item, rate, index }) => {
  const change = rate?.mcx_change ?? 0;
  const pct = rate?.mcx_change_pct ?? 0;
  const up = change >= 0;

  return (
    <motion.a
      href="tel:+918000093073"
      data-testid={`metal-card-${item.symbol.toLowerCase()}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      className={`group relative overflow-hidden border bg-[#121212] transition-colors duration-500 ${
        item.featured ? "border-[#25D366]/40 hover:border-[#25D366]" : "border-white/10 hover:border-[#25D366]/50"
      } ${item.span}`}
    >
      {item.image && (
        <>
          <img
            src={item.image}
            alt={item.symbol}
            className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-[opacity,transform] duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        </>
      )}

      <div className={`relative z-10 flex h-full flex-col justify-between p-6 ${item.featured ? "min-h-[300px]" : "min-h-[210px]"}`}>
        <div className="flex items-start justify-between">
          <span className="flex items-center gap-2">
            <span
              className="font-ticker text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 border"
              style={{ color: item.accent, borderColor: `${item.accent}55` }}
            >
              {item.symbol}
            </span>
            {item.featured && (
              <span className="inline-flex items-center gap-1 font-ticker text-[9px] tracking-[0.2em] uppercase bg-[#25D366] text-[#0A0A0A] px-2 py-1 font-bold">
                <Star size={9} /> Speciality
              </span>
            )}
          </span>
          <ArrowUpRight
            size={18}
            className="text-white/30 group-hover:text-[#25D366] group-hover:translate-x-1 group-hover:-translate-y-1 transition-[color,transform] duration-300"
          />
        </div>

        <div>
          <p className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1.5">
            {rate?.name ?? item.symbol} · MCX + LME
          </p>
          <p className={`font-display font-extrabold tracking-tight text-[#FDFDFD] ${item.featured ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}>
            {rate ? `₹${fmt(rate.mcx, rate.dec)}` : "—"}
          </p>
          {rate?.lme != null && (
            <p className="font-ticker text-sm text-white/55 mt-1">
              ${fmt(rate.lme, rate.lme_dec)} <span className="text-[10px] uppercase text-white/30">{rate.lme_unit}</span>
            </p>
          )}
          <p className={`mt-1.5 inline-flex items-center gap-1.5 font-ticker text-xs ${up ? "text-[#00C851]" : "text-[#FF3B30]"}`}>
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {up ? "+" : ""}
            {pct.toFixed(2)}% <span className="text-white/30 normal-case">{rate?.mcx_unit ?? ""}</span>
          </p>
        </div>
      </div>
    </motion.a>
  );
};

export default function MetalsSection({ rates }) {
  const { str } = useLang();
  const bySymbol = Object.fromEntries(rates.map((r) => [r.symbol, r]));

  return (
    <section
      id="metals"
      data-testid="metals-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-b border-white/10 noise"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
        <div>
          <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">
            {str.metalsTag}
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
            {str.metalsH1} <br />
            <span className="text-stroke-gold">{str.metalsH2}</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-white/50">{str.metalsPara}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 md:auto-rows-[170px] gap-4">
        {LAYOUT.map((item, i) => (
          <MetalCard key={item.symbol} item={item} rate={bySymbol[item.symbol]} index={i} />
        ))}
      </div>
    </section>
  );
}
